import { create } from 'zustand';

interface SelectedProfile {
  id: string;
  displayName: string;
}

interface ProfileStoreState {
  selectedProfile: SelectedProfile | null;
  select: (profile: SelectedProfile) => void;
  clear: () => void;
  initializeSelection: (profiles: SelectedProfile[]) => void;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isSelectedProfile(value: unknown): value is SelectedProfile {
  if (!isRecord(value)) {
    return false;
  }

  return typeof value['id'] === 'string' && typeof value['displayName'] === 'string';
}

function loadFromStorage(): SelectedProfile | null {
  try {
    const stored = localStorage.getItem('selectedProfile');
    if (stored) {
      const parsed: unknown = JSON.parse(stored);
      if (isSelectedProfile(parsed)) {
        return parsed;
      }
    }
  } catch {
    // Ignore parse errors
  }
  return null;
}

export const useProfileStore = create<ProfileStoreState>((set) => ({
  selectedProfile: loadFromStorage(),

  select: (profile) => {
    localStorage.setItem('selectedProfile', JSON.stringify(profile));
    set({ selectedProfile: profile });
  },

  clear: () => {
    localStorage.removeItem('selectedProfile');
    set({ selectedProfile: null });
  },

  initializeSelection: (profiles) => {
    if (profiles.length === 0) {
      localStorage.removeItem('selectedProfile');
      set({ selectedProfile: null });
      return;
    }

    if (profiles.length === 1) {
      const profile = profiles[0];
      if (profile) {
        localStorage.setItem('selectedProfile', JSON.stringify(profile));
        set({ selectedProfile: profile });
      }
      return;
    }

    const stored = loadFromStorage();
    if (stored && profiles.some((p) => p.id === stored.id)) {
      set({ selectedProfile: stored });
    } else {
      // Auto-select the first available profile
      const first = profiles[0];
      if (first) {
        localStorage.setItem('selectedProfile', JSON.stringify(first));
        set({ selectedProfile: first });
      }
    }
  },
}));
